import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Avatar,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Construction,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Engineering,
  SupervisorAccount,
  Security,
  Build,
  Business,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Test user credentials
  const testUsers = [
    {
      name: 'Admin',
      email: 'admin@construction.com',
      password: 'admin123',
      role: 'Administrator',
      icon: AdminPanelSettings,
    },
    {
      name: 'Manager',
      email: 'manager@construction.com',
      password: 'manager123',
      role: 'Project Manager',
      icon: Engineering,
    },
    {
      name: 'Supervisor',
      email: 'supervisor@construction.com',
      password: 'supervisor123',
      role: 'Site Supervisor',
      icon: SupervisorAccount,
    },
    {
      name: 'Safety',
      email: 'safety@construction.com',
      password: 'safety123',
      role: 'Safety Officer',
      icon: Security,
    },
    {
      name: 'Worker',
      email: 'worker@construction.com',
      password: 'worker123',
      role: 'Field Worker',
      icon: Build,
    },
    {
      name: 'Contractor',
      email: 'contractor@construction.com',
      password: 'contractor123',
      role: 'Contractor',
      icon: Business,
    }
  ];

  const handleQuickLogin = async (user: typeof testUsers[0]) => {
    setEmail(user.email);
    setPassword(user.password);
    setError('');
    setLoading(true);

    try {
      await login(user.email, user.password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#ffffff',
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: '100%',
            border: '1px solid #e0e0e0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            borderRadius: 1,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Avatar
                sx={{
                  m: 1,
                  bgcolor: '#000000',
                  width: 56,
                  height: 56,
                }}
              >
                <Construction fontSize="large" sx={{ color: '#ffffff' }} />
              </Avatar>
              
              <Typography component="h1" variant="h4" gutterBottom sx={{ color: '#000000', fontWeight: 300 }}>
                UpscaleSpaces
              </Typography>
              
              <Typography variant="h6" sx={{ color: '#666666', fontWeight: 300 }}>
                UpscaleSpaces
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#333333',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  },
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>

            <Divider sx={{ my: 3, borderColor: '#e0e0e0' }}>
              <Typography variant="body2" sx={{ color: '#666666', fontSize: '0.875rem' }}>
                Quick Login (Test Users)
              </Typography>
            </Divider>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
                mt: 2,
              }}
            >
              {testUsers.map((user) => {
                const IconComponent = user.icon;
                return (
                  <Button
                    key={user.email}
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={loading}
                    onClick={() => handleQuickLogin(user)}
                    startIcon={<IconComponent sx={{ fontSize: '1rem' }} />}
                    sx={{
                      py: 1,
                      fontSize: '0.75rem',
                      borderColor: '#000000',
                      color: '#000000',
                      fontWeight: 400,
                      '&:hover': {
                        borderColor: '#000000',
                        backgroundColor: '#f5f5f5',
                      },
                      '&:disabled': {
                        borderColor: '#e0e0e0',
                        color: '#9e9e9e',
                      },
                    }}
                  >
                    {user.role}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '0.75rem' }}>
                Click any button above for instant login with test credentials
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;